
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";

export interface SubscriptionData {
  plan: string;
  isPremium: boolean;
  translationUsage: {
    used: number;
    limit: number;
    percentage: number;
  };
  analysisUsage: {
    used: number;
    limit: number;
    percentage: number;
  };
  resetDate: Date | null;
}

export function useSubscription() {
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    async function fetchSubscriptionData() {
      try {
        setIsLoading(true);
        
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get user usage data from the RPC function
        const { data, error } = await supabase.rpc('get_user_current_usage', {
          user_uuid: user.id
        });

        if (error) {
          console.error("Error fetching subscription data:", error);
          throw error;
        }

        if (data && data.length > 0) {
          // Since Supabase RPC returns an array with a single object, we get the first item
          const usageData = data[0];
          
          // Get user plan information
          const { data: profileData, error: profileError } = await supabase
            .from("profiles")
            .select("subscription_plan")
            .eq("id", user.id)
            .single();

          if (profileError) {
            console.error("Error fetching profile data:", profileError);
            throw profileError;
          }

          const isPremium = profileData.subscription_plan === "Premium";
          const subscriptionInfo: SubscriptionData = {
            plan: profileData.subscription_plan,
            isPremium,
            translationUsage: {
              used: usageData.translation_chars_used,
              limit: usageData.translation_char_limit,
              percentage: (usageData.translation_chars_used / usageData.translation_char_limit) * 100
            },
            analysisUsage: {
              used: usageData.analysis_tokens_used,
              limit: usageData.analysis_token_limit,
              percentage: (usageData.analysis_tokens_used / usageData.analysis_token_limit) * 100
            },
            resetDate: usageData.reset_date ? new Date(usageData.reset_date) : null
          };

          setSubscriptionData(subscriptionInfo);
        }
      } catch (error: any) {
        console.error("Subscription fetch error:", error);
        toast({
          title: "Error loading subscription data",
          description: error.message || "Please try again later",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchSubscriptionData();
  }, [toast]);

  // Function to format the reset date to month name and day
  const formatResetDate = (date: Date | null): string => {
    if (!date) return "";
    return format(date, "MMMM d");
  };

  return {
    subscriptionData,
    isLoading,
    formatResetDate
  };
}
